import { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';

import { IMaterialPrice } from '../../types/material';
import { ICustomerCategory, ICustomerLight } from '../../types/customer';
import { ISelectOption } from '../../types/list';
import { IVendorLight } from '../../types/vendor';

import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';

import { CustomerService } from '../../services/customerService';
import { UtilityService } from '../../services/utilityService';
import { VendorService } from '../../services/vendorService';
import { MaterialPriceService } from '../../services/materialPriceService';

import NumberField from '../shared/numberField';
import Json from '../json';

import '../../styles/materialPriceDetailsForm.scss';

const MaterialPriceDetailsForm = () => {
	const navigate = useNavigate();
	const listContext = useListContext();
	const defaultUomId = parseInt(listContext.unitsOfMeasurement.find(u => u.name == "pounds").value);
	const defaultContainerId = parseInt(listContext.containers.find(c => c.name == "None")?.id ?? listContext.containers[0].id);
	const [formFields, setFormFields] = useState<IMaterialPrice>({
		customerId: 0,
		vendorId: 0,
		materialId: Number.parseInt(listContext.materials[0]?.value),
		wasteType: "Fees",
		recycleMethod: "Landfill",
		customerCategoryId: 0,
		profile: '',
		externalPriceId: "",
		externalMaterialName: undefined,
		price: 0,
		startDate: new Date(),
		notes: "",
		uomId: defaultUomId,
		uomName: listContext.unitsOfMeasurement[0]?.name,
		priceByContainer: false,
		containerId: undefined,
		containerPrice: undefined,
		customerMaterialId: undefined,
	});
	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingVendors, setIsLoadingVendors] = useState(true);
	const [isLoadingCategories, setIsLoadingCategories] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [customerOptions, setCustomerOptions] = useState<ICustomerLight[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [currentVendors, setCurrentVendors] = useState<IVendorLight[]>([]);
	const [vendorUsesExternalPriceId, setVendorUsesExternalPriceId] = useState<boolean>(false);
	const [pricingMetric, setPricingMetric] = useState<"weight" | "container">("weight");
	const [customerCategories, setCustomerCategories] = useState<ICustomerCategory[]>([]);
	const recycleMethods: string[] = [
		"A22K",
		"A99DB",
		"CBPS",
		"CCRC",
		"CCRNE",
		"CCRKS",
		"CCSS",
		"CNOS",
		"Energy Recovery",
		"Fuel Blending",
		"H040",
		"H061",
		"H100",
		"H141",
		"Incineration",
		"Land Treatment",
		"Landfill",
		"LCCRQ",
		"Metals Recovery",
		"Neutralization",
		"Neutralization Only",
		"Not Reportable",
		"Reclaim",
		"Recycle",
		"Rental",
		"Reuse",
		"Solvents Recovery",
		"Support",
		"Transport",
		"Water Treatment",
		"WTE",
		"Other",
	];

	const wasteTypes: string[] = [
		"Fees",
		"Industrial",
		"Production",
		"Rental",
		"Special Project",
		"Other",
	];

	const brandsWithCustomMaterialIds: string[] = [
	];

	useEffect(() => {
		getCustomerOptions();
	}, [])

	useEffect(() => {
		if (formFields.customerId != 0) {
			getVendorOptions();
		}
	}, [formFields.customerId])

	useEffect(() => {
		if (formFields.vendorId != 0) {
			getCustomerCategories();
			setVendorUsesExternalPriceId(currentVendors.some(v => v.id == formFields.vendorId.toString() && v.usesExternalPriceIds));
		}
	}, [formFields.vendorId]);

	useEffect(() => {
		setFormFields(prev => ({
			...prev,
			["priceByContainer"]: pricingMetric == "container",
			["price"]: pricingMetric == "container" ? 0 : prev.price,
			["containerId"]: pricingMetric == "container" ? defaultContainerId : undefined,
			["containerPrice"]: pricingMetric == "container" ? prev.containerPrice ?? 0 : undefined,
		}));
	}, [pricingMetric])

	const getCustomerOptions = () => {
		setIsLoadingCustomers(true);
		CustomerService.getCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);

				let activeCustomers: ICustomerLight[] = response.data.filter((item: ICustomerLight) => { return item.status == 'Active' });
				setCustomerOptions(activeCustomers.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (activeCustomers.length > 0) {
					setFormFields({ ...formFields, ["customerId"]: parseInt(activeCustomers[0].id.toString()) })
				}
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	const getVendorOptions = () => {
		setIsLoadingVendors(true);
		VendorService.getVendorsByCustomerId(formFields.customerId)
			.then((response) => {
				console.log('VendorService.getVendorsByCustomerId: SUCCESS', response);
				setCurrentVendors(response.data);
				let options: ISelectOption[] = [];
				response.data.map((item: IVendorLight) => {
					options.push({
						"name": item.name,
						"value": item.id
					})
				})
				setVendorOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					// If the previously selected vendor is in this list, just leave it selected
					if (!options.some(v => +v.value == formFields.vendorId)) {
						setFormFields({ ...formFields, ["vendorId"]: parseInt(options[0].value) });
					}
					else {
						getCustomerCategories();
					}
				}
				else {
					setFormFields({ ...formFields, ["vendorId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('VendorService.getVendorsByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
				setIsLoadingVendors(false);
			});
	}

	const getSelectedUom = () => {
		return listContext.unitsOfMeasurement.find(u => u.value == formFields.uomId?.toString())?.name ?? "Unit";
	}

	const getCustomerCategories = () => {
		setIsLoadingCategories(true);
		CustomerService.getCustomerCategoriesByCustomerAndVendor(formFields.customerId, formFields.vendorId)
			.then((response) => {
				console.log('CustomerService.getCustomerCategories: SUCCESS', response);
				const categories = CustomerService.mapCustomerCategories(response.data);
				setCustomerCategories(categories.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (categories.length > 0) {
					// If the previously selected category is in this list, just leave it selected
					if (!categories.some(cc => cc.id == formFields.customerCategoryId)) {
						setFormFields({ ...formFields, ["customerCategoryId"]: categories[0].id ?? -1 });
					}
				}
				else {
					setFormFields({ ...formFields, ["customerCategoryId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('CustomerService.getCustomerCategories: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingCategories(false);
			});
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (customerBrandHasCustomMaterialIds() && (formFields.customerMaterialId ?? 0) < 1) {
			setErrorMessage(`${customerOptions.find(c => c.id == formFields.customerId.toString())?.brand ?? "Customer"} requires a custom Material ID for this material that is greater than 0.`);
			window.scrollTo(0, 0);
			return;
		}

		setErrorMessage(undefined);
		setIsSaving(true);

		MaterialPriceService.addMaterialPrice(formFields)
			.then((response) => {
				console.log('MaterialPriceService.addMaterialPrice: SUCCESS', response);
				navigate("/materialprice");
			})
			.catch((error) => {
				console.log('MaterialPriceService.addMaterialPrice: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const handleCustomerChange = (event: SelectChangeEvent<number>) => {
		let customerMaterialId = formFields.customerMaterialId;
		if (!customerBrandHasCustomMaterialIds(+event.target.value)) {
			customerMaterialId = undefined;
			if (errorMessage?.includes("requires a custom Material ID for this material that is greater than 0.")) {
				setErrorMessage(undefined);
			}
		}
		setFormFields({ ...formFields, ["customerId"]: Number.parseInt(event.target.value.toString()), ["customerMaterialId"]: customerMaterialId });
	}

	const handleStartDateChange = (value: Dayjs | null) => {
		if (value) {
			let newFields = formFields;
			newFields.startDate = value.toDate();
			setFormFields(newFields);
		}
	}

	const customerBrandHasCustomMaterialIds = (customerId?: number): boolean => {
		return brandsWithCustomMaterialIds?.map(c => c.toLowerCase())?.includes(customerOptions?.find(c => c.id == (customerId ?? formFields.customerId)?.toString())?.brand?.toLowerCase()) ?? false;
	}

	return (
		<section className="hwi-material-price-details-form">
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>{(isLoadingCustomers || isLoadingCustomers) ? "Loading..." : "Customer *"}</InputLabel>
						<Select
							value={formFields.customerId <= 0 ? '' : formFields.customerId}
							className="customer-field"
							required
							onChange={handleCustomerChange}
							disabled={isLoadingCustomers || isLoadingCustomers}
						>
							{customerOptions.map((item, index) => (
								<MenuItem value={item.id} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>{(isLoadingCustomers || isLoadingVendors) ? "Loading..." : "Vendor *"}</InputLabel>
						<Select
							value={formFields.vendorId == -1 || isLoadingCustomers || isLoadingVendors ? '' : formFields.vendorId}
							className="vendor-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["vendorId"]: Number.parseInt(event.target.value.toString()) }) }}
							disabled={isLoadingCustomers || isLoadingVendors}
						>
							{vendorOptions.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
						{(formFields.vendorId == -1) &&
							<p className="help-text">The selected customer has not beed assigned to any vendors.</p>
						}
					</div>
					<div className="row">
						<InputLabel>Material (Waste Category - Name) *</InputLabel>
						<Select
							value={formFields.materialId}
							className="material-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["materialId"]: Number.parseInt(event.target.value.toString()) }) }}
						>
							{listContext.materials.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>{(isLoadingCustomers || isLoadingVendors || isLoadingCategories) ? "Loading..." : "Customer Code (Report Sort) *"}</InputLabel>
						<Select
							value={formFields.customerCategoryId == -1 || isLoadingCustomers || isLoadingVendors || isLoadingCategories ? '' : formFields.customerCategoryId}
							className="category-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["customerCategoryId"]: Number.parseInt(event.target.value.toString()) }) }}
							disabled={isLoadingCustomers || isLoadingVendors || isLoadingCategories}
						>
							{customerCategories.map((item, index) => (
								<MenuItem value={item.id?.toString()} key={index}>{item.name}</MenuItem>
							))}
						</Select>
						{(formFields.customerCategoryId == -1) &&
							<p className="help-text">The selected customer does not have any codes.</p>
						}
					</div>
					<div className="row">
						<InputLabel>Process *</InputLabel>
						<Select
							value={formFields.wasteType}
							className="waste-type-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["wasteType"]: event.target.value }) }}
						>
							{wasteTypes.map((item, index) => (
								<MenuItem value={item} key={index}>{item}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Disposal Type *</InputLabel>
						<Select
							value={formFields.recycleMethod}
							className="recycle-method-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["recycleMethod"]: event.target.value }) }}
						>
							{recycleMethods.map((item, index) => (
								<MenuItem value={item} key={index}>{item}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Profile</InputLabel>
						<TextField className="profile-field"
							name="profile"
							type="text"
							inputProps={{
								maxLength: 50,
							}}
							onBlur={(event) => { setFormFields({ ...formFields, ["profile"]: event.target.value }) }}
						></TextField>
					</div>
					{customerBrandHasCustomMaterialIds() &&
						<div className="row">
							<InputLabel>{customerOptions?.find(c => c.id == formFields.customerId.toString())?.brand} Material ID *</InputLabel>
							<NumberField className="customer-material-id-field"
								name="customer-material-id"
								min={1}
								required
								onFocus={(event) => event.target.select()}
								onBlur={(event) => { setFormFields({ ...formFields, ["customerMaterialId"]: +event.target.value }) }}
								value={formFields.customerMaterialId ?? ''}
							></NumberField>
							{(customerBrandHasCustomMaterialIds() && (formFields.customerMaterialId ?? 0) < 1) &&
								<p className="help-text">{customerOptions.find(c => c.id == formFields.customerId.toString())?.brand ?? "Customer"} requires a custom Material ID for this material that is greater than 0.</p>
							}
						</div>
					}
					<div className="row">
						<InputLabel>Price By *</InputLabel>
						<Select
							value={formFields.priceByContainer ? "container" : "weight"}
							className="price-by-field"
							required
							onChange={(event) => { setPricingMetric(event.target.value as "weight" | "container") }}
						>
							<MenuItem value="container">Container</MenuItem>
							<MenuItem value="weight">Weight</MenuItem>
						</Select>
					</div>
					{formFields.priceByContainer &&
						<>
							<div className="row">
								<InputLabel>Container *</InputLabel>
								<Select
									value={(formFields.containerId ?? 0) == 0 ? '' : formFields.containerId}
									className="container-field"
									required
									onChange={(event) => { console.log(event.target); setFormFields({ ...formFields, ["containerId"]: Number.parseInt(event.target.value.toString()) }) }}
								>
									{listContext.containers.map((item, index) => (
										<MenuItem value={item.id?.toString()} key={index}>{item.name}</MenuItem>
									))}

								</Select>
							</div>
							<div className="row">
								<InputLabel>Container Price *</InputLabel>
								<NumberField className="container-price-field"
									name="container-price"
									precision={5}
									required
									onFocus={(event) => event.target.select()}
									onBlur={(event) => { setFormFields({ ...formFields, ["containerPrice"]: +event.target.value }) }}
									value={formFields.containerPrice}
								></NumberField>
							</div>
						</>
					}
					{vendorUsesExternalPriceId &&
						<>
							<div className="row">
								<InputLabel>Vendor Material ID *</InputLabel>
								<TextField className="vendor-material-id-field"
									required
									name="vendor-material-id"
									type="text"
									inputProps={{
										maxLength: 20,
									}}
									onBlur={(event) => { setFormFields({ ...formFields, ["externalPriceId"]: event.target.value }) }}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Vendor Material Name</InputLabel>
								<TextField className="vendor-material-name-field"
									required
									name="vendor-material-name"
									type="text"
									inputProps={{
										maxLength: 100,
									}}
									onBlur={(event) => { setFormFields({ ...formFields, ["externalMaterialName"]: event.target.value }) }}
								></TextField>
							</div>
						</>
					}
					<div className="row">
						<InputLabel>Unit of Measure *</InputLabel>
						<Select
							value={formFields.uomId}
							className="unit-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["uomId"]: Number.parseInt(event.target.value.toString()) }) }}
						>
							{listContext.unitsOfMeasurement.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Price Per Unit ({getSelectedUom()}) *</InputLabel>
						<NumberField className="price-field"
							name="price"
							precision={5}
							required
							onFocus={(event) => event.target.select()}
							onBlur={(event) => { setFormFields({ ...formFields, ["price"]: +event.target.value }) }}
							value={formFields.price}
						></NumberField>
					</div>
					<div className="row">
						<FormControlLabel 
							className="gross-weight-field"
							control={
								<Checkbox 
									size='small' 
									checked={formFields.isGrossWeightMaterial ?? false} 
									onChange={() => setFormFields({ ...formFields, ["isGrossWeightMaterial"]: !formFields.isGrossWeightMaterial })}
								></Checkbox>} 
							label="This is a gross weight material. Tare weight should be included in the cost/credit calculation." />
					</div>
					<div className="row">
						<InputLabel>Effective Date</InputLabel>
						<DatePicker className="start-date-field" value={dayjs(formFields.startDate)} onChange={(value) => handleStartDateChange(value)} />
					</div>
					<div className="row">
						<InputLabel>Notes</InputLabel>
						<TextField className="notes-field"
							name="notes"
							value={formFields.notes}
							multiline={true}
							rows={3}
							onChange={(event) => { setFormFields({ ...formFields, ["notes"]: event.target.value }) }}
						>
						</TextField>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</LocalizationProvider>
		</section>
	)
}

export default MaterialPriceDetailsForm;